import { fetchWithChatFeedback } from './chatFeedbackRequired';
/**
 * Content-free, viewer-authorized edit-awareness projections for Market
 * Research.  This client deliberately rejects unknown fields so a backend
 * regression cannot turn the stepper or Activity panel into a transport for
 * stage payloads, snapshots, recipient lists, or acknowledgement state.
 */
export interface MarketResearchEditAwarenessStageSummary {
  stage_code: number;
  revision: number;
  has_unreviewed_other_edit: boolean;
  /** Optional additive detail; older servers safely yield an empty array. */
  unreviewed_other_edits: MarketResearchInlineEdit[];
}

/** A finite, server-projected description of one other-party stage update. */
export interface MarketResearchInlineEdit {
  event_id?: string;
  stage_code?: number;
  revision: number;
  action: string;
  component_key: string;
  actor_email: string;
  occurred_at: string;
  related_update_count: number;
}

export interface MarketResearchEditAwarenessSummary {
  stages: MarketResearchEditAwarenessStageSummary[];
}

export interface MarketResearchEditActivityEntry {
  event_id: string;
  stage_code: number;
  revision: number;
  stage_label: string;
  summary: string;
  actor: string;
  occurred_at: string;
  component_key?: string;
  related_update_count?: number;
}

export interface MarketResearchEditActivityPage {
  entries: MarketResearchEditActivityEntry[];
  earlier_cursor: string | null;
}

const projectionError = 'We could not load workflow updates. Please try again.';
const acknowledgementError = 'We could not record that stage view.';
const apiUrl = (endpoint: string): string => `${process.env.REACT_APP_API_URL || ''}${endpoint}`;
const canonicalUtcShape = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.\d{1,6})?Z$/;
const emailShape = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const maxPageSize = 25;
const knownStageCodes = new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]);
const stageLabels: Record<number, string> = {
  1: 'Requirement Description',
  2: 'Generate Questions',
  3: 'Product Discovery',
  4: 'PSC Assignment',
  5: 'NAICS Mapping',
  6: 'Vendor Discovery',
  7: 'Section B',
  8: 'Section C',
  9: 'Section D',
  10: 'Section E',
  11: 'Synopsis',
  12: 'Vendor Bids',
  13: 'DD 2579 Coordination',
  14: 'Brand-name J&A',
};
const knownActionSummaries = new Set([
  'Stage updated',
  'Stage regenerated',
  'Product selection changed',
  'Brand-name J&A updated',
  'Stage work cleared for rework',
]);
const knownEditActions = new Set([
  'stage_updated', 'stage_generated', 'cleared_for_rework',
  'questions_regenerated', 'question_list_edited', 'answers_changed',
  'requirement_document_changed', 'requirement_description_changed',
  'requirement_description_generated', 'requirement_intake_updated',
  'salient_characteristics_regenerated', 'salient_characteristics_edited',
  'product_selection_changed', 'brand_name_decision_changed', 'covered_products_changed',
  'search_parameters_changed', 'search_sources_changed', 'manual_codes_changed',
  'product_results_refreshed', 'product_discovery_updated',
  'psc_codes_regenerated', 'psc_assignments_updated', 'naics_mapping_regenerated', 'naics_mappings_updated',
  'vendor_search_rerun', 'vendor_search_criteria_changed', 'vendor_results_refreshed', 'vendor_selections_changed',
  'section_b_regenerated', 'section_b_edited', 'section_c_regenerated', 'section_c_edited',
  'section_d_regenerated', 'section_d_edited', 'section_e_regenerated', 'section_e_edited',
  'synopsis_regenerated', 'synopsis_edited', 'synopsis_questions_regenerated', 'synopsis_answers_changed',
  'line_items_changed', 'attachments_changed', 'vendor_quote_files_changed', 'bid_information_extracted',
  'bid_rows_edited', 'vendor_bids_updated', 'abstract_of_offers_edited',
  'dd2579_prefill_refreshed', 'coordination_form_edited', 'coordination_signatures_changed',
  'ja_acquisition_value_changed', 'ja_certifications_changed', 'ja_coordination_changed',
  'ja_approval_changed', 'ja_narrative_edited', 'ja_covered_facts_changed', 'ja_evidence_changed',
  'ja_document_regenerated', 'brand_name_ja_updated',
]);
const knownComponentKeys = new Set([
  'stage', 'requirement_document', 'requirement_description', 'questions', 'answers', 'editor', 'document',
  'search_controls', 'manual_codes', 'product_results', 'product_selection', 'brand_name',
  'assignment_controls', 'assignment_grid', 'mapping_controls', 'mapping_grid',
  'vendor_search', 'vendor_results', 'vendor_selection', 'considered_vendors', 'business_type_filters',
  'small_business_rationale', 'synopsis_questions', 'synopsis_answers', 'solicitation_form', 'line_items',
  'attachments', 'bid_files', 'bid_results', 'bid_table', 'abstract_editor', 'prefill', 'coordination_form',
  'coordination_rationale', 'coordination_signatures', 'acquisition_value', 'certifications', 'coordination',
  'approval', 'narrative', 'covered_products', 'evidence', 'document_status',
  // Server-owned descriptors for regions that currently use the stage-header
  // fallback. Keep these finite anchors valid even before their inline slots
  // are rendered, so new precise events never fail closed in the client.
  'grid', 'results', 'selections', 'quote_files', 'form', 'covered_facts',
]);
const legacyActionToFinite: Record<string, string> = {
  'Stage updated': 'stage_updated',
  'Stage regenerated': 'stage_generated',
  'Product selection changed': 'product_selection_changed',
  'Brand-name J&A updated': 'brand_name_ja_updated',
  'Stage work cleared for rework': 'cleared_for_rework',
  'Questions regenerated': 'questions_regenerated',
  'Question list edited': 'question_list_edited',
  'Answers changed': 'answers_changed',
  'Requirement document added or replaced': 'requirement_document_changed',
  'Requirement description changed': 'requirement_description_changed',
  'Requirement description generated from document': 'requirement_description_generated',
  'Requirement intake updated': 'requirement_intake_updated',
  'Salient Characteristics regenerated': 'salient_characteristics_regenerated',
  'Salient Characteristics edited': 'salient_characteristics_edited',
  'Brand-name decision changed': 'brand_name_decision_changed',
  'Covered products changed': 'covered_products_changed',
  'Search parameters changed': 'search_parameters_changed',
  'Search sources changed': 'search_sources_changed',
  'Manual PSC/NAICS inputs changed': 'manual_codes_changed',
  'Product results refreshed': 'product_results_refreshed',
  'Product Discovery updated': 'product_discovery_updated',
  'PSC codes regenerated': 'psc_codes_regenerated',
  'PSC assignments updated': 'psc_assignments_updated',
  'NAICS mapping regenerated': 'naics_mapping_regenerated',
  'NAICS mappings updated': 'naics_mappings_updated',
  'Vendor search rerun': 'vendor_search_rerun',
  'Vendor search criteria changed': 'vendor_search_criteria_changed',
  'Vendor results refreshed': 'vendor_results_refreshed',
  'Vendor selections changed': 'vendor_selections_changed',
  'Section B regenerated': 'section_b_regenerated', 'Section B edited': 'section_b_edited',
  'Section C regenerated': 'section_c_regenerated', 'Section C edited': 'section_c_edited',
  'Section D regenerated': 'section_d_regenerated', 'Section D edited': 'section_d_edited',
  'Section E regenerated': 'section_e_regenerated', 'Section E edited': 'section_e_edited',
  'Synopsis regenerated': 'synopsis_regenerated',
  'Synopsis edited': 'synopsis_edited',
  'Synopsis questions regenerated': 'synopsis_questions_regenerated',
  'Synopsis answers changed': 'synopsis_answers_changed',
  'Line items changed': 'line_items_changed',
  'Attachments changed': 'attachments_changed',
  'Vendor quote files changed': 'vendor_quote_files_changed',
  'Bid information extracted': 'bid_information_extracted',
  'Bid rows edited': 'bid_rows_edited',
  'Vendor Bids updated': 'vendor_bids_updated',
  'Abstract of Offers edited': 'abstract_of_offers_edited',
  'DD/DAF 2579 prefill refreshed': 'dd2579_prefill_refreshed',
  'Coordination form edited': 'coordination_form_edited',
  'Coordination signatures changed': 'coordination_signatures_changed',
  'Acquisition value changed': 'ja_acquisition_value_changed',
  'Certifications changed': 'ja_certifications_changed',
  'Coordination details changed': 'ja_coordination_changed',
  'Approval changed': 'ja_approval_changed',
  'J&A narrative edited': 'ja_narrative_edited',
  'Covered products or brand-name facts changed': 'ja_covered_facts_changed',
  'Supporting evidence changed': 'ja_evidence_changed',
  'J&A document regenerated': 'ja_document_regenerated',
  'Vendor bid information updated': 'vendor_bid_information_updated',
  'DD/DAF 2579 updated': 'dd2579_updated',
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const isCanonicalUtc = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const match = canonicalUtcShape.exec(value);
  if (!match) return false;
  const timestamp = Date.parse(`${match[1]}Z`);
  return Number.isFinite(timestamp)
    && new Date(timestamp).toISOString().slice(0, 19) === match[1];
};

const isSafeStageCode = (value: unknown): value is number => (
  typeof value === 'number' && Number.isSafeInteger(value) && knownStageCodes.has(value)
);

const parseInlineEdit = (value: unknown, expectedStageCode?: number, allowLegacy = false): MarketResearchInlineEdit => {
  if (!isRecord(value)
    || Object.keys(value).some((key) => ![
      'event_id', 'stage_code', 'revision', 'action', 'component_key', 'actor_email', 'occurred_at', 'related_update_count',
    ].includes(key))
    || typeof value.revision !== 'number' || !Number.isSafeInteger(value.revision) || value.revision < 1
    || typeof value.action !== 'string'
    || (value.component_key !== undefined && (typeof value.component_key !== 'string' || !knownComponentKeys.has(value.component_key)))
    || (!allowLegacy && value.component_key === undefined)
    || typeof value.actor_email !== 'string' || !emailShape.test(value.actor_email) || value.actor_email.length > 320
    || !isCanonicalUtc(value.occurred_at)
    || (value.related_update_count !== undefined && (
      typeof value.related_update_count !== 'number' || !Number.isSafeInteger(value.related_update_count)
      || value.related_update_count < 1 || value.related_update_count > 1000
    ))
    || (!allowLegacy && value.related_update_count === undefined)
    || (value.event_id !== undefined && (typeof value.event_id !== 'string' || value.event_id.length < 1 || value.event_id.length > 200))
    || (value.stage_code !== undefined && !isSafeStageCode(value.stage_code))
    || (expectedStageCode !== undefined && value.stage_code !== undefined && value.stage_code !== expectedStageCode)) {
    throw new Error(projectionError);
  }
  const action = legacyActionToFinite[value.action] ?? value.action;
  if (!knownEditActions.has(action)) throw new Error(projectionError);
  return {
    event_id: typeof value.event_id === 'string' ? value.event_id : undefined,
    stage_code: typeof value.stage_code === 'number' ? value.stage_code : expectedStageCode,
    revision: value.revision,
    action,
    component_key: typeof value.component_key === 'string' ? value.component_key : 'stage',
    actor_email: value.actor_email.trim().toLowerCase(),
    occurred_at: value.occurred_at,
    related_update_count: typeof value.related_update_count === 'number' ? value.related_update_count : 1,
  };
};

const parseSummary = (value: unknown): MarketResearchEditAwarenessSummary => {
  if (!isRecord(value)
    || Object.keys(value).some((key) => key !== 'stages')
    || !Array.isArray(value.stages)
    || value.stages.length > knownStageCodes.size) {
    throw new Error(projectionError);
  }
  const seen = new Set<number>();
  const stages = value.stages.map((stage): MarketResearchEditAwarenessStageSummary => {
    if (!isRecord(stage)
      || Object.keys(stage).some((key) => !['stage_code', 'revision', 'has_unreviewed_other_edit', 'unreviewed_other_edits'].includes(key))
      || !isSafeStageCode(stage.stage_code)
      || seen.has(stage.stage_code)
      || typeof stage.revision !== 'number'
      || !Number.isSafeInteger(stage.revision)
      || stage.revision < 0
      || typeof stage.has_unreviewed_other_edit !== 'boolean'
      || (stage.has_unreviewed_other_edit && stage.revision < 1)
      || (stage.unreviewed_other_edits !== undefined && !Array.isArray(stage.unreviewed_other_edits))) {
      throw new Error(projectionError);
    }
    const stageRevision = stage.revision as number;
    const stageCode = stage.stage_code as number;
    const unreviewedOtherEdits = (stage.unreviewed_other_edits ?? []).map((entry) => parseInlineEdit(entry, stageCode));
    if (unreviewedOtherEdits.some((entry) => entry.revision > stageRevision)
      || (stage.has_unreviewed_other_edit && stage.unreviewed_other_edits !== undefined && unreviewedOtherEdits.length === 0)) {
      throw new Error(projectionError);
    }
    seen.add(stage.stage_code);
    return {
      stage_code: stageCode,
      revision: stageRevision,
      has_unreviewed_other_edit: stage.has_unreviewed_other_edit,
      unreviewed_other_edits: unreviewedOtherEdits,
    };
  });
  return { stages };
};

const parseActivity = (value: unknown): MarketResearchEditActivityPage => {
  if (!isRecord(value)
    || Object.keys(value).some((key) => !['events', 'earlier_cursor'].includes(key))
    || !Array.isArray(value.events)
    || value.events.length > maxPageSize
    || !(value.earlier_cursor === null || typeof value.earlier_cursor === 'string')) {
    throw new Error(projectionError);
  }
  const ids = new Set<string>();
  const entries = value.events.map((entry): MarketResearchEditActivityEntry => {
    if (!isRecord(entry)
      || Object.keys(entry).some((key) => ![
        'event_id', 'stage_code', 'revision', 'actor_email', 'action', 'occurred_at', 'stage_label', 'component_key', 'related_update_count',
      ].includes(key))
      || typeof entry.event_id !== 'string' || !entry.event_id || ids.has(entry.event_id)
      || !isSafeStageCode(entry.stage_code)
      || typeof entry.revision !== 'number' || !Number.isSafeInteger(entry.revision) || entry.revision < 1
      || (entry.stage_label !== undefined && entry.stage_label !== stageLabels[entry.stage_code])
      || !isCanonicalUtc(entry.occurred_at)) {
      throw new Error(projectionError);
    }
    // Activity has its own, strictly allowlisted projection.  The server adds
    // stage_label solely for this Activity display; do not broaden the shared
    // inline-edit parser to accept it (or any future Activity-only field).
    // Instead, project the already-validated intersection before parsing.
    const inline = parseInlineEdit({
      event_id: entry.event_id,
      stage_code: entry.stage_code,
      revision: entry.revision,
      action: entry.action,
      component_key: entry.component_key,
      actor_email: entry.actor_email,
      occurred_at: entry.occurred_at,
      related_update_count: entry.related_update_count,
    }, entry.stage_code, true);
    ids.add(entry.event_id);
    return {
      event_id: entry.event_id,
      stage_code: entry.stage_code,
      revision: entry.revision,
      stage_label: stageLabels[entry.stage_code],
      summary: actionDisplay(inline.action),
      actor: inline.actor_email,
      occurred_at: entry.occurred_at,
      component_key: inline.component_key,
      related_update_count: inline.related_update_count,
    };
  });
  for (let index = 1; index < entries.length; index += 1) {
    if (Date.parse(entries[index].occurred_at) > Date.parse(entries[index - 1].occurred_at)) {
      throw new Error(projectionError);
    }
  }
  return { entries, earlier_cursor: value.earlier_cursor };
};

const actionDisplay = (action: string): string => {
  const display: Record<string, string> = {
    stage_updated: 'Stage updated', stage_generated: 'Stage regenerated', cleared_for_rework: 'Stage work cleared for rework',
    questions_regenerated: 'Questions regenerated', question_list_edited: 'Question list edited', answers_changed: 'Answers changed',
    product_selection_changed: 'Product selection changed', brand_name_ja_updated: 'Brand-name J&A updated',
  };
  return display[action] ?? action.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const requestProjection = async (endpoint: string, signal?: AbortSignal): Promise<unknown> => {
  let response: Response;
  try {
    response = await fetchWithChatFeedback(apiUrl(endpoint), {
      method: 'GET', credentials: 'include', headers: { Accept: 'application/json' }, signal,
    });
  } catch (error) {
    // The Activity panel owns cancellation. Preserve its abort signal so a
    // superseded request can be silent instead of becoming a generic error.
    if (signal?.aborted || (error instanceof DOMException && error.name === 'AbortError')) throw error;
    throw new Error(projectionError);
  }
  if (response.status === 401) window.dispatchEvent(new Event('auth-expired'));
  if (!response.ok) throw new Error(projectionError);
  return response.json().catch(() => null);
};

export async function getMarketResearchEditAwarenessSummary(
  flowId: string,
): Promise<MarketResearchEditAwarenessSummary> {
  return parseSummary(await requestProjection(
    `/api/market-research/flows/${encodeURIComponent(flowId)}/edit-awareness/summary`,
  ));
}

export async function acknowledgeMarketResearchStageEdit(
  flowId: string,
  stageCode: number,
  revision: number,
): Promise<void> {
  if (!isSafeStageCode(stageCode) || !Number.isSafeInteger(revision) || revision < 1) {
    throw new Error(acknowledgementError);
  }
  let response: Response;
  try {
    response = await fetchWithChatFeedback(apiUrl(
      `/api/market-research/flows/${encodeURIComponent(flowId)}/edit-awareness/acknowledgements`,
    ), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage_code: stageCode, revision }),
    });
  } catch {
    throw new Error(acknowledgementError);
  }
  if (response.status === 401) window.dispatchEvent(new Event('auth-expired'));
  if (!response.ok) throw new Error(acknowledgementError);
}

export async function getMarketResearchEditActivity(
  flowId: string,
  before?: string | null,
  signal?: AbortSignal,
): Promise<MarketResearchEditActivityPage> {
  const params = new URLSearchParams({ limit: String(maxPageSize) });
  if (before) params.set('before', before);
  return parseActivity(await requestProjection(
    `/api/market-research/flows/${encodeURIComponent(flowId)}/edit-awareness/activity?${params.toString()}`,
    signal,
  ));
}
